import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Browser } from '@capacitor/browser';

import { MOPLA_SOLUTIONS_HOME_URL } from '@mopla/constants';
import { ESubscriptionStatus, TSubscription } from '@mopla/data-models';
import {
	BillIcon,
	CreditCardIcon,
	EditIconV2,
	PauseIcon,
	TooltipIcon,
} from '@mopla/ui';

import { usePassengerAppNavigate } from '../../../../hooks/usePassengerAppNavigate';

import { TOptionItem, TOptionsConfig } from './types';

// TODO: redesign-dticket - implement onClick handlers
export const useDTicketOptionsConfig = (
	subscription: TSubscription
): TOptionsConfig => {
	const { t } = useTranslation('userDTicket');
	const {
		navigateToSubscriptionEditPause,
		navigateToSubscriptionPause,
		navigateToSubscriptionEdit,
		navigateToDownloadInvoices,
		navigateToEditPaymentMethod,
	} = usePassengerAppNavigate();

	const optionsConfig = useMemo(() => {
		if (!subscription) return null;

		const isInvalid = subscription.status === ESubscriptionStatus.INVALID;
		const hasPendingCancel = !!subscription.canceledAt;
		// The backend forbids revoking the cancellation (e.g. the subscription was
		// cancelled by an admin or payment failed). Pausing, editing a pause and editing the
		// subscription are all unavailable while that is the case.
		const { disallowCancelCancellation } = subscription;

		const options: TOptionItem[] = [];

		const isPauseScheduled = !!(
			subscription.pauseStart && subscription.pauseEnd
		);
		const isPaused = subscription.status === ESubscriptionStatus.PAUSED;
		const isActive = subscription.status === ESubscriptionStatus.ACTIVE;

		// First option varies based on subscription status and scheduled actions.
		// Invalid subscriptions (failed payment) cannot be paused,
		// so the first option is excluded from the list.
		if (!isInvalid && !disallowCancelCancellation) {
			if (isPaused || isPauseScheduled) {
				options.push({
					id: 'edit-pause',
					label: t('list_options.edit_pause'),
					onClick: () => {
						navigateToSubscriptionEditPause();
					},
					icon: <PauseIcon />,
				});
			} else if (isActive) {
				options.push({
					id: 'pause',
					label: t('list_options.pause_subscription'),
					onClick: () => {
						navigateToSubscriptionPause();
					},
					icon: <PauseIcon />,
				});
			}
		}

		// Common options for all statuses.
		// "Edit subscription" is excluded when invalid with a pending cancellation —
		// that state has no available action, so the edit page would be empty.
		if (!(isInvalid && hasPendingCancel) && !disallowCancelCancellation) {
			options.push({
				id: 'edit',
				label: t('list_options.edit_subscription'),
				onClick: () => {
					navigateToSubscriptionEdit();
				},
				icon: <EditIconV2 />,
			});
		}

		options.push(
			{
				id: 'payment',
				label: t('list_options.manage_payment_method'),
				onClick: () => {
					navigateToEditPaymentMethod();
				},
				icon: <CreditCardIcon />,
			},
			{
				id: 'invoices',
				label: t('list_options.download_invoices'),
				onClick: () => {
					navigateToDownloadInvoices();
				},
				icon: <BillIcon />,
			},
			{
				id: 'help',
				label: t('list_options.request_help'),
				onClick: () => {
					Browser.open({ url: MOPLA_SOLUTIONS_HOME_URL });
				},
				icon: <TooltipIcon />,
			}
		);

		return options;
	}, [
		navigateToDownloadInvoices,
		navigateToSubscriptionEdit,
		navigateToEditPaymentMethod,
		navigateToSubscriptionEditPause,
		navigateToSubscriptionPause,
		subscription,
		t,
	]);

	return optionsConfig;
};
